<template>
  <div>
    <ListCard :items="knowledgeBtns" @onClick="onClickKnowledgeBtn" />
    <section class="campaing_filters my_14">
      <div class="campaing_filter">
        <SearchInput
          v-model="searchValue"
          :placeholder="$t('CAMPAIGN.EVALUATIONS.FILTERS.SEARCH_PLACEHOLDER')"
          @search="onSearch"
        />
      </div>
      <div class="campaing_filter filters__button-wrap">
        <div v-if="appliedFilters.length" class="filters__applied-indicator" />
        <woot-button
          class="margin-right-small clear"
          color-scheme="secondary"
          data-testid="create-new-contact"
          icon="filter"
          @click="onToggleFilter"
        >
          {{ $t('CONTACTS_PAGE.FILTER_CONTACTS') }}
        </woot-button>
      </div>
    </section>

    <KnowledgeTable
      :cols="cols"
      :records="records"
      :is-loading="uiFlags.isKnowledgeFetching"
      :sort-config="sortConfig"
      @on-sort-change="onSortChange"
      @on-edit-click="openEditPopup"
      @on-delete-click="openDeletePopup"
    />

    <div class="loadMore_btn">
      <woot-button
        variant="clear"
        size="small"
        color-scheme="primary"
        :is-loading="uiFlags.isFetching"
        :is-disabled="paginationCompute.isLastPage"
        @click="onPageChange(Number(meta.current_page) + 1)"
      >
        {{ paginationCompute.btnText }}
      </woot-button>
    </div>

    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="onToggleFilter"
      size="medium"
    >
      <KnowledgeAdvancedFilter
        v-if="showFiltersModal"
        :on-close="onToggleFilter"
        :filter-items="filterItems"
        :initial-applied-filters="appliedFilters"
        :filter-attributes="filterAttributes"
        @applyFilter="onApplyFilter"
        @clearFilters="clearFilters"
      />
    </woot-modal>

    <woot-modal :show.sync="isModalOpen" :on-close="closeModal" size="medium">
      <AddUrlModal
        v-if="
          (isAddModalOpen || isEditModalOpen) &&
            createType === KNOWLEDGE_TYPE.URL
        "
        :initial-item="editRow"
        @onClose="closeAddModal"
        @onSubmit="fetchRecordList({ page: 1 })"
      />
      <AddFileModal
        v-if="
          (isAddModalOpen || isEditModalOpen) &&
            createType === KNOWLEDGE_TYPE.FILE
        "
        :initial-item="editRow"
        @onClose="closeAddModal"
        @onSubmit="fetchRecordList({ page: 1 })"
      />
    </woot-modal>
    <DeleteModal
      :show="isDeleteModalOpen"
      :title="$t('AI.KNOWLEDGE.DELETE_MODAL.TITLE')"
      :message="$t('AI.KNOWLEDGE.DELETE_MODAL.MESSAGE')"
      :confirm-text="$t('AI.KNOWLEDGE.DELETE_MODAL.CONFIRM_TEXT')"
      :reject-text="$t('AI.KNOWLEDGE.DELETE_MODAL.REJECT_TEXT')"
      :on-close="onCloseDeleteModal"
      :on-confirm="onConfirmDelete"
    />
  </div>
</template>

<script>
import ListCard from '../tools/ListCard.vue';
import KnowledgeTable from './KnowledgeTable.vue';
import {
  KNOWLEDGE_TYPE,
  knowledgeFilterTypes,
  KnowledgeFilterAttributList,
} from '../ai.constant';
import { mapGetters } from 'vuex';
import SearchInput from 'dashboard/components/ui/SearchInput.vue';
import alertMixin from 'shared/mixins/alertMixin';
import helperMixin from 'shared/mixins/helperMixin';
import DeleteModal from 'components/widgets/modal/DeleteModal.vue';
import AddFileModal from './AddFileModal.vue';
import AddUrlModal from './AddUrlModal.vue';
import KnowledgeAdvancedFilter from './KnowledgeAdvancedFilter.vue';

export default {
  components: {
    ListCard,
    KnowledgeTable,
    SearchInput,
    DeleteModal,
    AddUrlModal,
    AddFileModal,
    KnowledgeAdvancedFilter,
  },
  mixins: [helperMixin, alertMixin],
  data() {
    return {
      createType: null,
      KNOWLEDGE_TYPE,
      knowledgeBtns: [
        // { id: KNOWLEDGE_TYPE.URL, title: 'Add URL', icon: 'globe' },
        { id: KNOWLEDGE_TYPE.FILE, title: 'Add Files', icon: 'document' },
      ],
      isModalOpen: false,
      deleteRow: null,
      isAddModalOpen: false,
      isDeleteModalOpen: false,
      isEditModalOpen: false,
      editRow: null,
      searchValue: '',
      searchDebounceTimer: null,
      sortConfig: { name: 'asc' },
      showFiltersModal: false,
      filterItems: knowledgeFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`${filter.attributeI18nKey}`),
      })),
      appliedFilters: [],
      cols: [
        { key: 'name', name: 'Name', visible: true, type: 'default' },
        { key: 'type', name: 'type', visible: true, type: 'default' },
        { key: 'detail', name: 'Detail', visible: true, type: 'default' },
        {
          key: 'created_at',
          name: 'Created at',
          visible: true,
          type: 'default',
        },
        { key: 'action', name: 'action', visible: true, type: 'default' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'tools/getUIFlags',
      meta: 'tools/getMetaKnowledge',
      records: 'tools/getKnowledges',
    }),
    paginationCompute() {
      const totalPages = Math.ceil(this.meta.count / this.meta.page_size);
      const isLastPage = this.meta.current_page >= totalPages;
      const currentCount = Math.min(
        this.meta.current_page * this.meta.page_size,
        this.meta.count
      );
      const btnText = `${this.$t(
        'CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.AGENT_CALL_REPORT.LOAD_MORE_BTN'
      )} (${currentCount}/${this.meta.count})`;

      return {
        isLastPage,
        btnText,
      };
    },
    filterAttributes() {
      return KnowledgeFilterAttributList.map(el => ({
        ...el,
        name: this.$t(el.name),
      }));
    },
  },
  watch: {
    searchValue() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchRecordList({ page: 1 });
      }, 500);
    },
  },
  mounted() {
    this.fetchRecordList({ page: 1 });
  },
  methods: {
    fetchRecordList(query = {}) {
      try {
        const defautQuery = {
          page: this.meta.current_page,
          page_size: this.meta.page_size,
          q: this.searchValue,
          payload: this.appliedFilters,
          sort: this.getSortAttribute(this.sortConfig),
          ...query,
        };
        // prepare filter payload
        defautQuery.payload = defautQuery.payload.map(el => {
          const temp = { ...el };
          if (el.attribute_key === 'kind') {
            // Map kind to numeric values: document/file = 0, url = 1
            const kindValue = el.values.id === 'url' ? 1 : 0;
            temp.values = [kindValue];
            return temp;
          }
          temp.values = [el.values];
          return temp;
        });

        if (defautQuery.payload?.length) {
          this.$store.dispatch(
            'tools/filterKnowledges',
            this.cleanObject(defautQuery)
          );
        } else {
          delete defautQuery.payload;
          this.$store.dispatch(
            'tools/fetchKnowledges',
            this.cleanObject(defautQuery)
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching knowledge records:', error);
      }
    },
    onPageChange(page) {
      this.fetchRecordList({ page });
    },
    onSearch() {
      // Search is now handled by the watcher with debounce
      // This method is kept for compatibility with the @search event
    },
    getSortAttribute(newSort = {}) {
      let sortAttr = Object.keys({ ...this.sortConfig, ...newSort }).reduce(
        (acc, sortKey) => {
          const sortOrder = this.sortConfig[sortKey];
          if (sortOrder) {
            const sortOrderSign = sortOrder === 'asc' ? '' : '-';
            return `${sortOrderSign}${sortKey}`;
          }
          return acc;
        },
        ''
      );
      if (!sortAttr) {
        this.sortConfig = { name: 'asc' };
        sortAttr = 'name';
      }
      return sortAttr;
    },
    onSortChange(params) {
      console.error('Sort change params:', params);

      let sortDetails = {};
      Object.keys(params).forEach(key => {
        if (params[key]) {
          sortDetails = {};
          sortDetails.key = key;
          sortDetails.order = params[key];
        }
      });

      this.sortConfig = params;
      const sort = this.getSortAttribute(sortDetails);
      this.fetchRecordList({ sort, page: 1 });
    },
    onToggleFilter() {
      this.showFiltersModal = !this.showFiltersModal;
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters;
      this.fetchRecordList({ page: 1 });
      this.showFiltersModal = false;
    },
    clearFilters() {
      this.appliedFilters = [];
      this.fetchRecordList({ page: 1 });
      this.showFiltersModal = false;
    },
    onClickKnowledgeBtn(toolBtn) {
      this.createType = toolBtn?.id;
      this.openAddModal();
    },
    openAddModal() {
      this.isAddModalOpen = true;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.isAddModalOpen = false;
      this.isEditModalOpen = false;
      this.editRow = null;
      this.isDeleteModalOpen = false;
    },
    closeAddModal() {
      this.isAddModalOpen = false;
      this.editRow = null;
      this.isModalOpen = false;
    },
    openEditPopup(row) {
      this.editRow = row;
      this.isAddModalOpen = false;
      this.isEditModalOpen = true;
      this.createType =
        row.kind === 'url' ? KNOWLEDGE_TYPE.URL : KNOWLEDGE_TYPE.FILE;
      this.isModalOpen = true;
    },
    async openDeletePopup(row) {
      this.deleteRow = row;
      this.isDeleteModalOpen = true;
    },
    onCloseDeleteModal() {
      this.deleteRow = null;
      this.isDeleteModalOpen = false;
      this.isModalOpen = false;
    },
    async onConfirmDelete() {
      try {
        await this.$store.dispatch('tools/deleteKnowledgeById', {
          knowledge_id: this.deleteRow?.id,
        });
        this.onCloseDeleteModal();
        this.showAlert(
          this.$t('AI.KNOWLEDGE.DELETE_MODAL.ALERTS.DELETE_SUCCESS')
        );
      } catch (error) {
        console.error('Error confirming delete:', error);
        this.showErrAlert(error, 'Something went wrong!');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.loadMore_btn button {
  display: block;
  margin: auto;
}
.campaing_filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  .campaing_filter {
    p {
      margin-bottom: 8px;
    }
  }
}

.filters__button-wrap {
  position: relative;

  .filters__applied-indicator {
    position: absolute;
    height: var(--space-small);
    width: var(--space-small);
    top: var(--space-smaller);
    right: var(--space-slab);
    background-color: var(--s-500);
    border-radius: var(--border-radius-rounded);
  }
}
</style>
